// frontend/src/contexts/AuthContext.js
import React, { createContext, useContext, useEffect, useState } from 'react';
import { auth } from '../config/firebase';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
  GoogleAuthProvider,
  signInWithPopup,
  sendPasswordResetEmail
} from 'firebase/auth';

const AuthContext = createContext();

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userToken, setUserToken] = useState(null);

  // Sign up function
  async function signup(email, password, displayName) {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      
      // Update user profile with display name
      await updateProfile(result.user, {
        displayName: displayName
      });

      // Get Firebase token for backend auth
      const token = await result.user.getIdToken();
      setUserToken(token);

      // Create user in backend database
      await createUserInBackend(result.user, token);

      return result;
    } catch (error) {
      console.error('Signup error:', error);
      throw error;
    }
  }

  // Login function
  async function login(email, password) {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      const token = await result.user.getIdToken();
      setUserToken(token);
      
      // Update user's last login in backend
      await updateUserInBackend(result.user, token);
      
      return result;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  // Google Sign In
  async function signInWithGoogle() {
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const token = await result.user.getIdToken();
      setUserToken(token);
      
      // Create or update user in backend
      await createUserInBackend(result.user, token);
      
      return result;
    } catch (error) {
      console.error('Google sign in error:', error);
      throw error;
    }
  }

  // Logout function
  async function logout() {
    try {
      setUserToken(null);
      return await signOut(auth);
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  }

  // Reset password
  async function resetPassword(email) {
    return sendPasswordResetEmail(auth, email);
  }

  // Create user in backend database
  async function createUserInBackend(firebaseUser, token) {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://imvelaphi-production.up.railway.app'}/api/auth/create-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          displayName: firebaseUser.displayName || firebaseUser.email.split('@')[0],
          photoURL: firebaseUser.photoURL
        })
      });

      if (!response.ok) {
        console.error('Failed to create user in backend');
      }
    } catch (error) {
      console.error('Error creating user in backend:', error);
    }
  }

  // Update user in backend database
  async function updateUserInBackend(firebaseUser, token) {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://imvelaphi-production.up.railway.app'}/api/auth/update-user`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          displayName: firebaseUser.displayName,
          photoURL: firebaseUser.photoURL,
          lastLogin: new Date().toISOString()
        })
      });

      if (!response.ok) {
        console.error('Failed to update user in backend');
      }
    } catch (error) {
      console.error('Error updating user in backend:', error);
    }
  }

  // Get fresh token
  async function getToken() {
    if (currentUser) {
      const token = await currentUser.getIdToken(true);
      setUserToken(token);
      return token;
    }
    return null;
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setCurrentUser(user);
        const token = await user.getIdToken();
        setUserToken(token);
      } else {
        setCurrentUser(null);
        setUserToken(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userToken,
    signup,
    login,
    logout,
    signInWithGoogle,
    resetPassword,
    getToken,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}